import { useState, useEffect, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import BaseSelect from '../General/FormsComp/Select/BaseSelect';
import LoadingSpinner from '../Loader/load';
import { avisoDe, compParam, UFS, nomeContrato } from './format';
import { validarCelularBR, formatarCelular } from './celular';

// Leva 2B: o eixo do faturamento é o CONTRATO (um cliente pode ter N contratos,
// cada um com seus próprios destinatários/remetente/habilitado).
const Destinatarios = () => {
    const { setAlert } = useContext(UserContext);
    const [contratos, setContratos] = useState([]);
    const [contratoSel, setContratoSel] = useState(null);
    const [destinatarios, setDestinatarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ nome: '', email: '', tipo: 'para', telefone_whatsapp: '' });
    // Edição em linha do WhatsApp: { id, valor } do destinatário sendo editado.
    const [editWa, setEditWa] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [bulk, setBulk] = useState({ uf: 'RS', user_id: '' });
    const [bulkHab, setBulkHab] = useState({ uf: 'RS' });
    const [remSel, setRemSel] = useState('');
    const [mostrarVinculos, setMostrarVinculos] = useState(false);
    const [compEnvio, setCompEnvio] = useState('');
    const [enviandoAgora, setEnviandoAgora] = useState(false);

    const aviso = avisoDe(setAlert);

    // O endpoint de ativação não traz e-mail do remetente (só nome) — o e-mail vem
    // de /usuariosRemetentes, já carregado para o select. Ver task-9-report.md.
    const emailDoRemetente = (userId) => usuarios.find((u) => String(u.id) === String(userId))?.email;

    const carregarContratos = async () => {
        setLoading(true);
        try {
            const { data } = await apiUser.get('/faturamento/ativacao');
            if (data.success) { setContratos(data.dados || []); setLoading(false); return data.dados || []; }
        } catch { aviso('Erro ao carregar contratos.'); }
        setLoading(false);
        return [];
    };

    const carregarUsuarios = async () => {
        try {
            const { data } = await apiUser.get('/faturamento/usuariosRemetentes');
            if (data.success) setUsuarios(data.dados || []);
        } catch { aviso('Erro ao carregar usuários remetentes.'); }
    };

    useEffect(() => {
        carregarContratos();
        carregarUsuarios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const opcoesUsuarios = usuarios.map((u) => ({ id: u.id, nome: `${u.name} (${u.email})` }));

    const selecionar = async (id) => {
        const c = contratos.find((x) => String(x.id) === String(id));
        setContratoSel(c || null);
        setRemSel(String(c?.remetente_user_id ?? ''));
        setEditWa(null);
        if (!c) { setDestinatarios([]); return; }
        try {
            const { data } = await apiUser.get('/faturamento/destinatarios', { params: { id_contrato: c.id } });
            if (data.success) setDestinatarios(data.dados || []);
        } catch { aviso('Erro ao carregar destinatários.'); }
    };

    const toggleHabilitado = async () => {
        const novo = contratoSel.habilitado !== '1';
        try {
            const { data } = await apiUser.post('/faturamento/contratoHabilitado', { id_contrato: contratoSel.id, habilitado: novo });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) {
                const atualizado = { ...contratoSel, habilitado: novo ? '1' : '0' };
                setContratoSel(atualizado);
                setContratos(contratos.map((c) => (c.id === atualizado.id ? atualizado : c)));
            }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    // Tail comum dos handlers em massa: recarrega a lista e preserva o contrato selecionado.
    const recarregarMantendoSelecao = async () => {
        const novaLista = await carregarContratos();
        if (contratoSel) {
            const novo = novaLista.find((x) => String(x.id) === String(contratoSel.id));
            if (novo) { setContratoSel(novo); setRemSel(String(novo.remetente_user_id ?? '')); }
        }
    };

    const habilitarPorEstado = async (habilitado) => {
        try {
            const { data } = await apiUser.post('/faturamento/clienteHabilitadoPorEstado', { uf: bulkHab.uf, habilitado });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) await recarregarMantendoSelecao();
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const aplicarRemetentePorEstado = async () => {
        if (!bulk.user_id) { aviso('Selecione o usuário remetente.'); return; }
        try {
            const { data } = await apiUser.post('/faturamento/remetentePorEstado', { uf: bulk.uf, user_id: bulk.user_id });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) await recarregarMantendoSelecao();
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const salvarRemetenteContrato = async (user_id) => {
        try {
            const { data } = await apiUser.post('/faturamento/remetente', { id_contrato: contratoSel.id, user_id: user_id || null });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) {
                const usuarioEscolhido = user_id ? usuarios.find((u) => String(u.id) === String(user_id)) : null;
                const atualizado = {
                    ...contratoSel,
                    remetente_user_id: usuarioEscolhido?.id || null,
                    remetente_nome: usuarioEscolhido?.name || null,
                };
                setContratoSel(atualizado);
                setContratos(contratos.map((c) => (c.id === atualizado.id ? atualizado : c)));
                setRemSel(String(usuarioEscolhido?.id ?? ''));
            }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    // Ajusta os contadores qtd_para/qtd_cc do contrato em memória (sem GET /ativacao).
    const patchContagem = (idContrato, tipo, delta) => {
        const campo = tipo === 'cc' ? 'qtd_cc' : 'qtd_para';
        const patch = (c) => (String(c.id) === String(idContrato) ? { ...c, [campo]: Number(c[campo] || 0) + delta } : c);
        setContratos((prev) => prev.map(patch));
        setContratoSel((prev) => (prev ? patch(prev) : prev));
    };

    const adicionar = async () => {
        if (!form.email.trim()) { aviso('Informe o e-mail.'); return; }
        if (form.telefone_whatsapp.trim()) {
            const cel = validarCelularBR(form.telefone_whatsapp);
            if (!cel.ok) { aviso(`WhatsApp: ${cel.erro}`); return; }
        }
        const tipo = form.tipo;
        try {
            const { data } = await apiUser.post('/faturamento/destinatarios', { id_contrato: contratoSel.id, ...form });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) {
                setForm({ nome: '', email: '', tipo: 'para', telefone_whatsapp: '' });
                await selecionar(contratoSel.id);
                patchContagem(contratoSel.id, tipo, +1);
            }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    // Salva (ou remove, se vazio) o celular de WhatsApp do lembrete do ciclo.
    const salvarWa = async () => {
        const valor = editWa.valor.trim();
        if (valor) {
            const cel = validarCelularBR(valor);
            if (!cel.ok) { aviso(cel.erro); return; }
        }
        try {
            const { data } = await apiUser.post('/faturamento/destinatarios/telefone', { id: editWa.id, telefone_whatsapp: valor });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) {
                const novo = data.dados?.telefone_whatsapp ?? null;
                setDestinatarios((prev) => prev.map((d) => (d.id === editWa.id ? { ...d, telefone_whatsapp: novo } : d)));
                setEditWa(null);
            }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const enviarAgora = async () => {
        setEnviandoAgora(true);
        try {
            const { data } = await apiUser.post('/faturamento/ciclo/enviar', {
                id_contrato: contratoSel.id,
                competencia: compParam(compEnvio),
            });
            aviso(data.message, data.success ? 'success' : 'warning');
        } catch { aviso('Erro ao comunicar com o servidor.'); }
        setEnviandoAgora(false);
    };

    const remover = async (id) => {
        const tipoRemovido = destinatarios.find((d) => String(d.id) === String(id))?.tipo;
        try {
            const { data } = await apiUser.post('/faturamento/destinatarios/remover', { id });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) {
                await selecionar(contratoSel.id);
                if (tipoRemovido) patchContagem(contratoSel.id, tipoRemovido, -1);
            }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const vinculados = contratos.filter((c) => c.remetente_user_id);

    if (loading) return <LoadingSpinner />;

    return (
        <div className='w-full max-w-4xl mx-auto'>

            {/* Banner de alerta: contratos habilitados sem destinatário PARA */}
            {(() => {
                const semDest = contratos.filter((c) => c.habilitado === '1' && Number(c.qtd_para) === 0);
                if (semDest.length === 0) return null;
                const nomes = semDest.slice(0, 3).map((c) => `${c.cliente_nome} — ${nomeContrato(c)}`).join(', ');
                const extra = semDest.length > 3 ? ` +${semDest.length - 3}` : '';
                return (
                    <div className='mb-4 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-yellow-900 text-sm'>
                        ⚠ {semDest.length} contrato(s) habilitado(s) sem destinatário PARA: {nomes}{extra}
                    </div>
                );
            })()}

            {/* Vincular remetente por estado (bulk) */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <p className='font-semibold dark:text-white mb-1'>Vincular remetente por estado</p>
                <p className='text-xs text-gray-500 dark:text-gray-400 mb-3'>
                    O e-mail do relatório sai do endereço do usuário vinculado (mesmo provedor). Sem vínculo, sai de noreply@voxcity.com.br.
                </p>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-2 items-end'>
                    <div>
                        <label className='block text-xs font-semibold mb-1 dark:text-gray-300'>Estado</label>
                        <select
                            className='w-full border rounded-lg p-2 dark:bg-gray-600 dark:text-white'
                            value={bulk.uf}
                            onChange={(e) => setBulk({ ...bulk, uf: e.target.value })}
                        >
                            {UFS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className='block text-xs font-semibold mb-1 dark:text-gray-300'>Usuário remetente</label>
                        <BaseSelect
                            name='remetenteEstado'
                            options={opcoesUsuarios}
                            value={bulk.user_id}
                            onChange={(e) => setBulk({ ...bulk, user_id: e.target.value })}
                            placeholder='Selecione o usuário'
                        />
                    </div>
                    <button
                        onClick={aplicarRemetentePorEstado}
                        className='px-4 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg font-semibold'
                    >
                        Aplicar ao estado
                    </button>
                </div>
                <hr className='border-gray-200 dark:border-gray-600 my-3' />
                <p className='font-semibold dark:text-white mb-2'>Habilitar envio por estado</p>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-2 items-end'>
                    <div>
                        <label className='block text-xs font-semibold mb-1 dark:text-gray-300'>Estado</label>
                        <select
                            className='w-full border rounded-lg p-2 dark:bg-gray-600 dark:text-white'
                            value={bulkHab.uf}
                            onChange={(e) => setBulkHab({ uf: e.target.value })}
                        >
                            {UFS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div className='flex gap-2 md:col-span-2'>
                        <button
                            onClick={() => habilitarPorEstado(true)}
                            className='flex-1 px-4 py-2 text-white rounded-lg font-semibold'
                            style={{ backgroundColor: '#9ac31c' }}
                        >
                            Habilitar estado
                        </button>
                        <button
                            onClick={() => habilitarPorEstado(false)}
                            className='flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold'
                        >
                            Desabilitar estado
                        </button>
                    </div>
                </div>
            </div>

            {/* Listagem de vínculos de remetente (colapsável) */}
            <div className='mb-4'>
                <button onClick={() => setMostrarVinculos(!mostrarVinculos)} className='text-sm text-vox-blue-600 hover:underline'>
                    {mostrarVinculos ? 'Ocultar vínculos de remetente' : `Ver vínculos de remetente (${vinculados.length} contrato(s))`}
                </button>
                {mostrarVinculos && (
                    <table className='w-full text-sm mt-2'>
                        <thead><tr className='bg-gray-100 dark:bg-gray-700 text-left dark:text-white'>
                            <th className='p-2'>Cliente</th><th className='p-2'>Contrato</th><th className='p-2'>Remetente</th><th className='p-2'>Habilitado</th>
                        </tr></thead>
                        <tbody>
                            {vinculados.map((c) => {
                                const email = emailDoRemetente(c.remetente_user_id);
                                return (
                                    <tr key={c.id} className='border-b dark:border-gray-700 dark:text-gray-200'>
                                        <td className='p-2'>{c.cliente_nome}</td>
                                        <td className='p-2'>{nomeContrato(c)}</td>
                                        <td className='p-2'>{c.remetente_nome}{email ? ` (${email})` : ''}</td>
                                        <td className='p-2'>{c.habilitado === '1' ? 'Sim' : 'Não'}</td>
                                    </tr>
                                );
                            })}
                            {vinculados.length === 0 && <tr><td colSpan={4} className='p-3 text-center text-gray-500'>Nenhum vínculo.</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Seletor de contrato */}
            <div className='mb-4'>
                <label className='block text-sm font-semibold mb-1 dark:text-white'>Cliente / Contrato</label>
                <BaseSelect
                    name='contrato'
                    options={contratos.map((c) => ({
                        id: c.id,
                        nome: `${c.cliente_nome} — ${nomeContrato(c)}${c.habilitado === '1' ? ' ✓ habilitado' : ''}${c.habilitado === '1' && Number(c.qtd_para) === 0 ? ' ⚠ sem destinatário' : ''}`,
                    }))}
                    value={contratoSel?.id || ''}
                    onChange={(e) => selecionar(e.target.value)}
                    placeholder='Selecione um cliente/contrato'
                />
            </div>

            {contratoSel && (
                <>
                    {/* Habilitar / desabilitar envio */}
                    <div className='flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                        <div>
                            <p className='font-semibold dark:text-white'>{contratoSel.cliente_nome} — {nomeContrato(contratoSel)}</p>
                            <p className='text-sm text-gray-600 dark:text-gray-300'>
                                Envio mensal: <b>{contratoSel.habilitado === '1' ? 'HABILITADO' : 'desabilitado'}</b>
                            </p>
                        </div>
                        <button onClick={toggleHabilitado}
                            className={`px-4 py-2 rounded-lg text-white font-semibold ${contratoSel.habilitado === '1' ? 'bg-red-500' : ''}`}
                            style={contratoSel.habilitado === '1' ? {} : { backgroundColor: '#9ac31c' }}>
                            {contratoSel.habilitado === '1' ? 'Desabilitar envio' : 'Habilitar envio'}
                        </button>
                    </div>

                    {/* Aviso: habilitado sem destinatário PARA */}
                    {contratoSel.habilitado === '1' && destinatarios.filter((d) => d.tipo === 'para').length === 0 && (
                        <div className='mb-4 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-yellow-900 text-sm'>
                            Este contrato está habilitado mas não tem destinatário PARA — o disparo vai falhar.
                        </div>
                    )}

                    {/* Remetente por contrato */}
                    <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                        <p className='font-semibold dark:text-white mb-1'>Remetente deste contrato</p>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-3'>
                            Atual:{' '}
                            <span className='font-medium dark:text-gray-200'>
                                {contratoSel.remetente_nome
                                    ? `${contratoSel.remetente_nome}${emailDoRemetente(contratoSel.remetente_user_id) ? ` (${emailDoRemetente(contratoSel.remetente_user_id)})` : ''}`
                                    : 'Padrão (noreply@voxcity.com.br)'}
                            </span>
                        </p>
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-2 items-end'>
                            <div className='md:col-span-2'>
                                <label className='block text-xs font-semibold mb-1 dark:text-gray-300'>Usuário remetente</label>
                                <BaseSelect
                                    name='remetenteContrato'
                                    options={opcoesUsuarios}
                                    value={remSel}
                                    onChange={(e) => setRemSel(e.target.value)}
                                    placeholder='Selecione o usuário'
                                />
                            </div>
                            <div className='flex gap-2'>
                                <button
                                    onClick={() => salvarRemetenteContrato(remSel)}
                                    className='flex-1 px-3 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg font-semibold text-sm'
                                >
                                    Salvar remetente
                                </button>
                                <button
                                    onClick={() => salvarRemetenteContrato(null)}
                                    className='flex-1 px-3 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg font-semibold text-sm'
                                >
                                    Usar padrão
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Enviar relatório agora */}
                    <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                        <p className='font-semibold dark:text-white mb-1'>Enviar relatório agora</p>
                        <p className='text-xs text-gray-600 dark:text-gray-300 mb-2'>
                            Usa exatamente o mesmo envio do disparo automático — bom para um teste real após configurar os destinatários.
                        </p>
                        <div className='flex items-end gap-2 flex-wrap'>
                            <div>
                                <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Competência (vazio = mês anterior)</label>
                                <input type='month' value={compEnvio} onChange={(e) => setCompEnvio(e.target.value)}
                                    className='border rounded-lg p-2 dark:bg-gray-600 dark:text-white' />
                            </div>
                            <button onClick={enviarAgora} disabled={enviandoAgora}
                                className='px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-50' style={{ backgroundColor: '#9ac31c' }}>
                                {enviandoAgora ? 'Enviando...' : 'Enviar relatório agora'}
                            </button>
                        </div>
                    </div>

                    {/* Formulário de destinatários */}
                    <div className='grid grid-cols-1 md:grid-cols-5 gap-2 mb-1'>
                        <input className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white' placeholder='Nome'
                            value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                        <input className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white' placeholder='E-mail'
                            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <input className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white' placeholder='WhatsApp (celular, opcional)'
                            value={form.telefone_whatsapp} onChange={(e) => setForm({ ...form, telefone_whatsapp: e.target.value })} />
                        <select className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'
                            value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                            <option value='para'>Destinatário principal</option>
                            <option value='cc'>Em cópia</option>
                        </select>
                        <button onClick={adicionar} className='px-4 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg'>Adicionar</button>
                    </div>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mb-4'>
                        Com o celular preenchido (DDD + 9 dígitos), o destinatário também recebe os lembretes do relatório por WhatsApp.
                    </p>

                    <div className='rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden'>
                        <table className='w-full table-fixed text-sm'>
                            <thead>
                                <tr className='bg-gradient-to-r from-vox-blue-600 to-vox-blue-800 text-white text-left'>
                                    <th className='px-4 py-3 w-1/4 font-medium uppercase tracking-wider text-xs'>Nome</th>
                                    <th className='px-4 py-3 font-medium uppercase tracking-wider text-xs'>E-mail</th>
                                    <th className='px-4 py-3 w-56 font-medium uppercase tracking-wider text-xs'>WhatsApp</th>
                                    <th className='px-4 py-3 w-24 text-center font-medium uppercase tracking-wider text-xs'>Tipo</th>
                                    <th className='px-4 py-3 w-28'></th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
                                {destinatarios.map((d) => (
                                    <tr key={d.id} className='dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'>
                                        <td className='px-4 py-2.5 truncate'>{d.nome || '—'}</td>
                                        <td className='px-4 py-2.5 truncate' title={d.email}>{d.email}</td>
                                        <td className='px-4 py-2.5'>
                                            {editWa && editWa.id === d.id ? (
                                                <div className='flex items-center gap-1'>
                                                    <input autoFocus className='w-32 border rounded p-1 text-xs dark:bg-gray-700 dark:text-white'
                                                        placeholder='(51) 99999-1234' value={editWa.valor}
                                                        onChange={(e) => setEditWa({ ...editWa, valor: e.target.value })}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') salvarWa(); if (e.key === 'Escape') setEditWa(null); }} />
                                                    <button onClick={salvarWa} className='text-vox-blue-600 hover:underline text-xs font-semibold'>salvar</button>
                                                    <button onClick={() => setEditWa(null)} className='text-gray-500 hover:underline text-xs'>cancelar</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => setEditWa({ id: d.id, valor: d.telefone_whatsapp ? formatarCelular(d.telefone_whatsapp) : '' })}
                                                    className='text-left hover:underline' title='Editar WhatsApp'>
                                                    {d.telefone_whatsapp ? formatarCelular(d.telefone_whatsapp) : <span className='text-gray-400 text-xs'>+ adicionar</span>}
                                                </button>
                                            )}
                                        </td>
                                        <td className='px-4 py-2.5 text-center'>
                                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${d.tipo === 'cc' ? 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200' : 'bg-vox-blue-100 text-vox-blue-800'}`}>
                                                {d.tipo === 'cc' ? 'CC' : 'PARA'}
                                            </span>
                                        </td>
                                        <td className='px-4 py-2.5 text-right'>
                                            <button onClick={() => remover(d.id)} className='text-red-500 hover:text-red-700 hover:underline text-xs font-semibold'>remover</button>
                                        </td>
                                    </tr>
                                ))}
                                {destinatarios.length === 0 && (
                                    <tr><td colSpan={5} className='p-6 text-center text-gray-500'>Nenhum destinatário cadastrado.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default Destinatarios;
